import React from 'react';
import { Button, Modal } from '@syneto/compass-react';
import { verdictDateLabel, verdictScopeLabel, verdictVersionLabel } from './complianceVerdictDisplay';

/*
 * Built out of Modal directly rather than on components/ActionModal, which has no way to disable
 * its primary action. Revoking enqueues a job that takes a moment to apply, and without the disable
 * a second click fires a second job against the same override.
 *
 * No type-the-serial-to-confirm gate, despite this being one-way. Revoking *restores* the default
 * compliance check rather than destroying anything: the row survives as audit history, and an
 * accidental revoke is recoverable by granting a new override. Stating the consequence plainly is
 * proportionate here; a friction gate would not be.
 */
const ComplianceVerdictRevokeModal = (props) => {
	const { verdict, componentTypes, componentTypesLoading, revokedBy, isSaving, onConfirm, onCancel } = props;

	return (
		<Modal show={Boolean(verdict)} onHide={onCancel}>
			<Modal.Header>
				<Modal.Title>Revoke override</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<p className="text-value">You are about to revoke this override:</p>

				<dl className="row">
					<dt className="col-4">Appliance</dt>
					<dd className="col-8">{verdict?.serialNumber}</dd>

					<dt className="col-4">Scope</dt>
					<dd className="col-8">{verdictScopeLabel(verdict, componentTypes, componentTypesLoading)}</dd>

					<dt className="col-4">Firmware version</dt>
					<dd className="col-8">{verdictVersionLabel(verdict)}</dd>

					{/*
					  * The reason is included deliberately: revoking withdraws that specific
					  * justification, and it is the only thing on screen saying why the exemption
					  * was granted in the first place.
					  */}
					<dt className="col-4">Reason</dt>
					<dd className="col-8">{verdict?.reason}</dd>

					<dt className="col-4">Granted by</dt>
					<dd className="col-8">{verdict?.grantedBy}</dd>

					{/*
					  * `createdAt` is when the verdict row was recorded, which for a granted failed check
					  * can be days before the grant - so it is labelled as the table labels it, not as
					  * "Granted". `updatedAt` is no substitute: the API defines it only as the last
					  * modification.
					  */}
					<dt className="col-4">Created</dt>
					<dd className="col-8">{verdictDateLabel(verdict?.createdAt)}</dd>
				</dl>

				<p className="mb-1">
					This appliance will be held to the firmware compliance rules again.
				</p>

				<p className="mb-1">
					This cannot be undone. The override stays in the list under the Revoked filter for audit, but
					it cannot be reinstated — granting a new override is the only way to exempt this appliance again.
				</p>

				<p className="text-muted mb-0">Recorded as revoked by <span className="text-value">{revokedBy}</span>.</p>
			</Modal.Body>

			<Modal.Footer>
				<Button role="secondary" onClick={onCancel}>Cancel</Button>
				<Button disabled={isSaving} onClick={onConfirm}>Revoke override</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default ComplianceVerdictRevokeModal;
